/**
 * Service for fetching application usage statistics.
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from '../auth/msalAuth';

// --- Response shape ---

export interface StatisticsCard {
  title: string;
  value: number;
}

export interface StatisticsHorizontalBars {
  labels: string[];
  values: number[];
}

export interface StatisticsResponse {
  cards: StatisticsCard[];
  horizontalBars: StatisticsHorizontalBars;
}

// --- Service ---

const REQUEST_TIMEOUT_MS = 8_000;
const MAX_RETRIES = 2;

export class StatisticsService {
  private static readonly API_BASE_URL = import.meta.env.VITE_APIM_BASE_URL|| '';
  private static readonly ENDPOINT = '/nav-api/statistics';

  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  /**
   * Get the access token for API requests (delegates to shared helper)
   */
  private async getAccessToken(): Promise<string> {
    return getAccessTokenHelper(this.msalInstance);
  }

  /**
   * Fetch platform usage statistics from the statistics endpoint.
   * Retries up to MAX_RETRIES times on timeouts, network errors and transient
   * server errors (5xx / 408 / 429). Client errors (other 4xx) are surfaced
   * immediately since retrying them cannot succeed.
   */
  public async getStatistics(scope: 'global' | 'personal' = 'global'): Promise<StatisticsResponse> {
    const accessToken = await this.getAccessToken();
    const endpoint = scope === 'personal'
      ? `${StatisticsService.ENDPOINT}/user`
      : StatisticsService.ENDPOINT;
    const url = `${StatisticsService.API_BASE_URL}${endpoint}`;

    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
          },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error;

        const isTimeout = error instanceof DOMException && (error as DOMException).name === 'AbortError';
        const isNetworkError = error instanceof TypeError;

        if ((!isTimeout && !isNetworkError) || attempt === MAX_RETRIES) {
          throw error;
        }

        const retryDelay = Math.min(500 * Math.pow(2, attempt), 2_000);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      // Server responded — retry transient failures, surface the rest immediately.
      if (!response.ok) {
        const detail = await response.text().then(
          (body) => {
            try {
              return (JSON.parse(body) as { detail?: string })?.detail;
            } catch {
              return undefined;
            }
          },
          () => undefined
        );
        const error = new Error(
          detail ?? `Statistics request failed with status ${response.status}`
        );
        const isTransient =
          response.status >= 500 || response.status === 408 || response.status === 429;

        if (!isTransient || attempt === MAX_RETRIES) {
          throw error;
        }

        lastError = error;
        const retryDelay = Math.min(500 * Math.pow(2, attempt), 2_000);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      return response.json() as Promise<StatisticsResponse>;
    }

    throw lastError;
  }
}

// --- Singleton factory ---

export const createStatisticsService = (msalInstance: IPublicClientApplication): StatisticsService => {
  return new StatisticsService(msalInstance);
};
